import type { CardContract } from '../../../finance/models/CardContract'
import type {
  PdvMobileAppSetting,
  DesktopAppSetting,
  DigitalMenuAppSetting,
  MobileAppSetting,
  ServerAppSetting,
  SmartMenuAppSetting,
  StoreAppSetting,
  TotemAppSetting,
  KdsMobileAppSetting
} from './AppSetting'
import type { SlimModel } from '~/domain/shared/slim/models/SlimModel'

export enum AppKind {
  Totem = 'totem',
  Desktop = 'terminal',
  Server = 'server',
  Store = 'store',
  Ordersheet = 'ordersheet-check',
  PdvMobile = 'pos-a',
  Mobile = 'mobile-app',
  DigitalMenu = 'digital-menu',
  KdsMobile = 'kds-android',
  PosTef = 'pos-tef',
  SmartMenu = 'smart-menu',
  Other = 'other'
}

export enum AppSource {
  Totem = 'totem',
  Store = 'store',
  ERP = 'erp',
  POS = 'pdv',
  SmartMenu = 'smart-menu'
}

export enum SmartMenuFunctionality {
  Registration = 'registration',
  DigitalMenu = 'digitalmenu'
}

/** Push notification sync status from backend/Firebase. */
export enum PushNotificationStatus {
  Synced = 0,
  Confirmed = 1,
  OnQueue = 2,
  WaitingConfirmation = 3,
  NotForwarded = 4
}

export interface AppDevice {
  model: string
}

export interface BaseApp extends SlimModel {
  kind?: AppKind
  imageUrl?: string
  photoBase64?: string
  cardContracts?: CardContract[]
  terminal?: string
  device?: AppDevice
  pushNotificationStatus?: PushNotificationStatus
  /** Set transiently after save when scale base product was auto-enabled in catalog */
  wasBaseProductAutoEnabledForSale?: boolean
}

export interface AppCatalogCategory {
  catalogCategoryId: number
  catalogCategoryName: string
  preparationTimeMinutes: number
}

export interface DigitalMenuApp extends BaseApp {
  kind: AppKind.DigitalMenu
  settings: DigitalMenuAppSetting
}

export interface TotemApp extends BaseApp {
  kind: AppKind.Totem
  settings: TotemAppSetting
}

export interface ServerApp extends BaseApp {
  kind: AppKind.Server
  settings: ServerAppSetting
}

export interface DesktopApp extends BaseApp {
  kind: AppKind.Desktop
  settings: DesktopAppSetting
}

export interface OrdersheetApp extends BaseApp {
  kind: AppKind.Ordersheet
}

export interface PdvMobileApp extends BaseApp {
  kind: AppKind.PdvMobile
  settings: PdvMobileAppSetting
}

export interface MobileApp extends BaseApp {
  kind: AppKind.Mobile
  settings: MobileAppSetting
}

export interface PosTefApp extends BaseApp {
  kind: AppKind.PosTef
}

export interface SmartMenuApp extends BaseApp {
  kind: AppKind.SmartMenu
  settings: SmartMenuAppSetting
}

export interface OtherApp extends BaseApp {
  kind: AppKind.Other
}

export interface StoreApp extends BaseApp {
  kind: AppKind.Store
  settings: StoreAppSetting
  domain?: string | null
  /** Só a matriz pode alterar `domain`; filial recebe `false`. */
  domainEditable?: boolean
  categories?: AppCatalogCategory[]
}

export interface KdsMobileApp extends BaseApp {
  kind: AppKind.KdsMobile
  settings: KdsMobileAppSetting
}

export type App = DigitalMenuApp | TotemApp | ServerApp | DesktopApp | OrdersheetApp | PdvMobileApp | MobileApp | PosTefApp | SmartMenuApp | OtherApp | StoreApp | KdsMobileApp

/** App kinds that support push notification sync (Totem, PdvMobile, Server, DigitalMenu). */
export const SYNCABLE_APP_KINDS: readonly AppKind[] = [
  AppKind.Totem,
  AppKind.PdvMobile,
  AppKind.Server,
  AppKind.DigitalMenu
]

export function isAppSyncable (app: App): boolean {
  return app.kind != null && SYNCABLE_APP_KINDS.includes(app.kind)
}

/** App kinds controlled by the legacy "available for sale" flag (PDV Android, Mobile, Desktop). */
export const posAttendanceKinds: readonly AppKind[] = [
  AppKind.PdvMobile,
  AppKind.Mobile,
  AppKind.Desktop
]
